import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Pagination, Spin, Typography } from 'antd';
import { DownOutlined, FileOutlined, FolderOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

const TEXT_PRIMARY = '#1f1f1f';
const TEXT_MUTED = '#595959';
const PAGE_SIZE = 12;
const TREE_ARROW_WIDTH = 14;
const TREE_CHECKBOX_SLOT = 22;
const TREE_DEPTH_INDENT = 20;
const TREE_COLUMN_GAP = 8;

const panelStyle = {
  flex: 1,
  minWidth: 280,
  border: '1px solid rgba(148, 22, 127, 0.25)',
  borderRadius: 8,
  background: '#faf8fb',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360,
  maxHeight: 520,
};

const panelBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 10px 4px',
};

const filterBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 10px 8px',
  borderBottom: '1px solid rgba(148, 22, 127, 0.1)',
  background: '#faf8fb',
};

const loadingPanelStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 280,
  padding: '40px 24px',
  border: '1px solid rgba(148, 22, 127, 0.2)',
  borderRadius: 8,
  background: 'linear-gradient(180deg, #ffffff 0%, #faf8fb 100%)',
  textAlign: 'center',
};

const sortTreeNodes = (nodes) => {
  nodes.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });
  nodes.forEach((node) => sortTreeNodes(node.children));
};

const buildFileTree = (nodes) => {
  const normalized = (nodes || [])
    .map((node) => {
      const path = String(node.path || '').trim();
      if (!path) return null;
      const parts = path.split('/');
      return {
        id: path,
        path,
        title: parts[parts.length - 1] || path,
        type: String(node.type || 'file').toLowerCase() === 'folder' ? 'folder' : 'file',
        parent_id: parts.length > 1 ? parts.slice(0, -1).join('/') : null,
        children: [],
      };
    })
    .filter(Boolean);

  const byPath = new Map(normalized.map((node) => [node.path, node]));
  const roots = [];
  normalized.forEach((node) => {
    if (node.parent_id && byPath.has(node.parent_id)) {
      byPath.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  sortTreeNodes(roots);
  return roots;
};

const filterTree = (nodes, predicate) => (
  nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children, predicate);
    if (predicate(node) || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, [])
);

const flattenTree = (nodes) => {
  const rows = [];
  const walk = (list) => {
    list.forEach((node) => {
      rows.push(node);
      walk(node.children);
    });
  };
  walk(nodes);
  return rows;
};

const collectDescendantIds = (id, childrenByParent) => {
  const result = [];
  const queue = [...(childrenByParent.get(id) || [])];
  while (queue.length) {
    const next = queue.shift();
    result.push(next);
    queue.push(...(childrenByParent.get(next) || []));
  }
  return result;
};

const GithubFileScopePicker = ({
  nodes = [],
  loading = false,
  selectedPaths = [],
  onChange,
  disabled = false,
  selectedPanelFooter = null,
  branchLabel = '',
}) => {
  const [searchText, setSearchText] = useState('');
  const [browsePage, setBrowsePage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [collapsedBrowse, setCollapsedBrowse] = useState(() => new Set());
  const [collapsedSelected, setCollapsedSelected] = useState(() => new Set());

  const normalizedNodes = useMemo(() => (
    (nodes || [])
      .map((node) => ({
        id: String(node.path || '').trim(),
        path: String(node.path || '').trim(),
        title: String(node.path || '').trim().split('/').pop() || String(node.path || '').trim(),
        type: String(node.type || 'file').toLowerCase() === 'folder' ? 'folder' : 'file',
        parent_id: (() => {
          const parts = String(node.path || '').trim().split('/');
          return parts.length > 1 ? parts.slice(0, -1).join('/') : null;
        })(),
      }))
      .filter((node) => node.id)
  ), [nodes]);

  const nodesById = useMemo(() => {
    const map = new Map();
    normalizedNodes.forEach((node) => map.set(node.id, node));
    return map;
  }, [normalizedNodes]);

  const nodesByPath = useMemo(() => {
    const map = new Map();
    normalizedNodes.forEach((node) => map.set(node.path, node));
    return map;
  }, [normalizedNodes]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    normalizedNodes.forEach((node) => {
      if (!node.parent_id) return;
      if (!map.has(node.parent_id)) map.set(node.parent_id, []);
      map.get(node.parent_id).push(node.id);
    });
    return map;
  }, [normalizedNodes]);

  const selectedIds = useMemo(() => {
    const next = new Set();
    (selectedPaths || []).forEach((rawPath) => {
      const path = String(rawPath || '').trim();
      const node = nodesByPath.get(path);
      if (!node) return;
      next.add(node.id);
      collectDescendantIds(node.id, childrenByParent).forEach((id) => next.add(id));
    });
    return next;
  }, [selectedPaths, nodesByPath, childrenByParent]);

  const fullTree = useMemo(() => buildFileTree(nodes), [nodes]);

  const browseTree = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return filterTree(fullTree, (node) => {
      if (!q) return true;
      return String(node.title || '').toLowerCase().includes(q)
        || String(node.path || '').toLowerCase().includes(q);
    });
  }, [fullTree, searchText]);

  const browseFlat = useMemo(() => flattenTree(browseTree), [browseTree]);

  const selectedTree = useMemo(() => (
    filterTree(fullTree, (node) => selectedIds.has(node.id))
  ), [fullTree, selectedIds]);

  const hasSelectedAncestor = useMemo(() => {
    const cache = new Map();
    const check = (id) => {
      if (cache.has(id)) return cache.get(id);
      const node = nodesById.get(id);
      if (!node?.parent_id) {
        cache.set(id, false);
        return false;
      }
      if (selectedIds.has(node.parent_id)) {
        cache.set(id, true);
        return true;
      }
      const viaParent = check(node.parent_id);
      cache.set(id, viaParent);
      return viaParent;
    };
    return check;
  }, [nodesById, selectedIds]);

  const emitPaths = (nextSelectedSet) => {
    const roots = [];
    nextSelectedSet.forEach((id) => {
      const node = nodesById.get(id);
      if (!node?.path) return;
      if (node.parent_id && nextSelectedSet.has(node.parent_id)) return;
      roots.push(node.path);
    });
    onChange?.(roots);
  };

  const toggleNodeId = (id, checked) => {
    if (disabled || loading) return;
    const next = new Set(selectedIds);
    const descendants = collectDescendantIds(id, childrenByParent);
    if (checked) {
      next.add(id);
      descendants.forEach((childId) => next.add(childId));
    } else {
      next.delete(id);
      descendants.forEach((childId) => next.delete(childId));
    }
    emitPaths(next);
  };

  const toggleAllVisible = (checked) => {
    if (disabled || loading) return;
    const next = new Set(selectedIds);
    browseTree.forEach((root) => {
      if (hasSelectedAncestor(root.id)) return;
      const descendants = collectDescendantIds(root.id, childrenByParent);
      if (checked) {
        next.add(root.id);
        descendants.forEach((childId) => next.add(childId));
      } else {
        next.delete(root.id);
        descendants.forEach((childId) => next.delete(childId));
      }
    });
    emitPaths(next);
  };

  const toggleCollapsed = (scope, id) => {
    const setter = scope === 'browse' ? setCollapsedBrowse : setCollapsedSelected;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setBrowsePage(1);
  }, [searchText]);

  const renderNodeLabel = (node, { muted = false } = {}) => (
    <span
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: muted ? TEXT_MUTED : TEXT_PRIMARY,
        display: 'block',
        paddingTop: 2,
      }}
    >
      {node.type === 'folder' ? (
        <FolderOutlined style={{ marginRight: 6, color: '#94167f' }} />
      ) : (
        <FileOutlined style={{ marginRight: 6, color: TEXT_MUTED }} />
      )}
      <span style={{ fontWeight: muted ? 500 : 600 }}>{node.title}</span>
      {node.path ? (
        <Text style={{ marginLeft: 6, fontSize: 11, color: TEXT_MUTED }}>({node.path})</Text>
      ) : null}
    </span>
  );

  const renderTreeNode = (node, { scope, depth = 0 }) => {
    const hasChildren = node.children.length > 0;
    const collapsedSet = scope === 'browse' ? collapsedBrowse : collapsedSelected;
    const expanded = hasChildren && !collapsedSet.has(node.id);
    const ancestorSelected = hasSelectedAncestor(node.id);
    const isSelected = selectedIds.has(node.id);

    return (
      <div key={node.id}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px minmax(0, 1fr)`,
            columnGap: TREE_COLUMN_GAP,
            alignItems: 'start',
            marginBottom: 4,
            paddingLeft: depth * TREE_DEPTH_INDENT,
          }}
        >
          <span
            style={{
              width: TREE_ARROW_WIDTH,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 5,
            }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleCollapsed(scope, node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCollapsed(scope, node.id);
                  }
                }}
                style={{ cursor: 'pointer', color: '#94167f', lineHeight: 1 }}
              >
                {expanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
              </span>
            ) : null}
          </span>
          <span
            style={{
              width: TREE_CHECKBOX_SLOT,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <Checkbox
              checked={isSelected}
              disabled={disabled || loading || ancestorSelected}
              onChange={(e) => toggleNodeId(node.id, e.target.checked)}
              style={{ margin: 0, padding: 0, lineHeight: 1 }}
            />
          </span>
          <span style={{ minWidth: 0, paddingTop: 1 }}>
            {renderNodeLabel(node, { muted: ancestorSelected })}
          </span>
        </div>
        {hasChildren && expanded
          ? node.children.map((child) => renderTreeNode(child, { scope, depth: depth + 1 }))
          : null}
      </div>
    );
  };

  const browseSelectionState = useMemo(() => {
    const selectableRoots = browseTree.filter((root) => !hasSelectedAncestor(root.id));
    if (!selectableRoots.length) return { checked: false, indeterminate: false };
    const fullySelected = selectableRoots.filter((root) => selectedIds.has(root.id)).length;
    return {
      checked: fullySelected === selectableRoots.length,
      indeterminate: fullySelected > 0 && fullySelected < selectableRoots.length,
    };
  }, [browseTree, hasSelectedAncestor, selectedIds]);

  const paginateRoots = (roots, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return roots.slice(start, start + PAGE_SIZE);
  };

  const browsePageRoots = paginateRoots(browseTree, browsePage);
  const selectedPageRoots = paginateRoots(selectedTree, selectedPage);

  const renderPagination = (total, page, onPageChange) => (
    <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148, 22, 127, 0.1)', background: '#faf8fb' }}>
      <Pagination
        size="small"
        current={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={onPageChange}
        showSizeChanger={false}
        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} roots`}
        disabled={disabled || loading}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={loadingPanelStyle}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16, fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>
          Loading repository files
        </Text>
        <Text style={{ display: 'block', marginTop: 6, fontSize: 13, color: TEXT_MUTED, maxWidth: 360, lineHeight: 1.55 }}>
          {branchLabel ? `Fetching folders and files for ${branchLabel}…` : 'Fetching folders and files…'}
        </Text>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Browse files ({normalizedNodes.length})
          </div>
          <Text style={{ display: 'block', padding: '2px 10px 6px', fontSize: 11, color: TEXT_MUTED }}>
            Select folders or files to include. Selecting a folder includes all nested files.
          </Text>
          <div style={filterBarStyle}>
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
              placeholder="Search paths…"
              value={searchText}
              disabled={disabled}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: '1 1 200px' }}
            />
          </div>
          <div style={{ padding: '0 10px 6px' }}>
            <Checkbox
              checked={browseSelectionState.checked}
              indeterminate={browseSelectionState.indeterminate}
              disabled={disabled || !browseTree.length}
              onChange={(e) => toggleAllVisible(e.target.checked)}
              style={{ color: TEXT_PRIMARY, fontSize: 12 }}
            >
              Select all visible ({browseFlat.length})
            </Checkbox>
          </div>
          <div style={panelBodyStyle}>
            {browsePageRoots.length ? (
              browsePageRoots.map((node) => renderTreeNode(node, { scope: 'browse' }))
            ) : (
              <div style={{ padding: '8px 4px' }}>
                <Text style={{ display: 'block', color: TEXT_PRIMARY, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  {normalizedNodes.length === 0 ? 'No files found' : 'No matching files'}
                </Text>
                <Text style={{ color: TEXT_MUTED, fontSize: 12, lineHeight: 1.55 }}>
                  {normalizedNodes.length === 0
                    ? 'This branch has no indexable files yet.'
                    : 'Try a different search.'}
                </Text>
              </div>
            )}
          </div>
          {browseTree.length > PAGE_SIZE
            ? renderPagination(browseTree.length, browsePage, setBrowsePage)
            : null}
        </div>

        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Selected ({selectedPaths?.length || 0})
          </div>
          <Text style={{ display: 'block', padding: '2px 10px 6px', fontSize: 11, color: TEXT_MUTED }}>
            {branchLabel ? `Scope for branch ${branchLabel}` : 'Selected folders and files for this branch.'}
          </Text>
          <div style={panelBodyStyle}>
            {selectedPageRoots.length ? (
              selectedPageRoots.map((node) => renderTreeNode(node, { scope: 'selected' }))
            ) : (
              <div style={{ padding: '8px 4px' }}>
                <Text style={{ color: TEXT_MUTED, fontSize: 12, lineHeight: 1.55 }}>
                  No folders or files selected yet.
                </Text>
              </div>
            )}
          </div>
          {selectedTree.length > PAGE_SIZE
            ? renderPagination(selectedTree.length, selectedPage, setSelectedPage)
            : null}
          {selectedPanelFooter ? (
            <div style={{ padding: '10px', borderTop: '1px solid rgba(148, 22, 127, 0.1)', display: 'flex', justifyContent: 'flex-end' }}>
              {selectedPanelFooter}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GithubFileScopePicker;
